import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Member {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  joined_at: string;
  last_active?: string;
}

const UserManagementPage: React.FC = () => {
  useDocumentTitle('User Management');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<Member[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'viewer',
  });
  const [orgId, setOrgId] = useState<number | null>(null);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const orgResponse = await organizationAPI.getCurrent();
      if (orgResponse.data.status === 'success') {
        const orgData = orgResponse.data.data;
        setOrgId(orgData.id);

        const membersResponse = await organizationAPI.getMembers(orgData.id);
        if (membersResponse.data.status === 'success') {
          setMembers(membersResponse.data.data.members || []);
        }
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load members:', error);
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!orgId) return;

    if (!inviteForm.email) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await organizationAPI.inviteMember(orgId, inviteForm);
      toast.success('Invitation sent successfully');
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'viewer' });
      loadMembers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleUpdateRole = async () => {
    if (!orgId || !selectedMember) return;

    try {
      await organizationAPI.updateMemberRole(orgId, selectedMember.id, selectedMember.role);
      toast.success('Role updated successfully');
      setShowEditModal(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error: any) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!orgId) return;

    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await organizationAPI.removeMember(orgId, memberId);
      toast.success('Member removed successfully');
      loadMembers();
    } catch (error: any) {
      toast.error('Failed to remove member');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'danger';
      case 'admin': return 'primary';
      case 'editor': return 'success';
      case 'viewer': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'inactive': return 'secondary';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading team members...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">User Management</h1>
            <p className="text-muted mb-0">Manage team members and their access permissions</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
            <i className="bi bi-person-plus me-2"></i>Invite Member
          </button>
        </div>

        {/* Stats */}
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-people text-primary fs-4"></i>
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Total Members</h6>
                    <h3 className="mb-0">{members.length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-success bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-person-check text-success fs-4"></i>
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Active</h6>
                    <h3 className="mb-0">{members.filter(m => m.status === 'active').length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-warning bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-clock text-warning fs-4"></i>
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Pending</h6>
                    <h3 className="mb-0">{members.filter(m => m.status === 'pending').length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-danger bg-opacity-10 rounded p-3 me-3">
                    <i className="bi bi-shield-check text-danger fs-4"></i>
                  </div>
                  <div>
                    <h6 className="text-muted mb-1">Admins</h6>
                    <h3 className="mb-0">{members.filter(m => m.role === 'owner' || m.role === 'admin').length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0">Team Members</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Member</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Last Active</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold">{member.name}</div>
                            <div className="text-muted small">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-${getRoleColor(member.role)}`}>
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusColor(member.status)}`}>
                          {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                        </span>
                      </td>
                      <td className="text-muted">
                        {new Date(member.joined_at).toLocaleDateString()}
                      </td>
                      <td className="text-muted">
                        {member.last_active
                          ? new Date(member.last_active).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="text-end">
                        {member.role !== 'owner' && (
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setSelectedMember(member);
                                setShowEditModal(true);
                              }}
                              title="Edit Role"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleRemoveMember(member.id)}
                              title="Remove"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                        {member.role === 'owner' && (
                          <span className="text-muted small">Owner</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Role Descriptions */}
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0">Role Permissions</h5>
          </div>
          <div className="card-body">
            <div className="row g-4">
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <h6 className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-danger">Owner</span>
                  </h6>
                  <ul className="list-unstyled small text-muted mb-0">
                    <li><i className="bi bi-check text-success me-1"></i>Full access</li>
                    <li><i className="bi bi-check text-success me-1"></i>Manage billing</li>
                    <li><i className="bi bi-check text-success me-1"></i>Delete organization</li>
                    <li><i className="bi bi-check text-success me-1"></i>Transfer ownership</li>
                  </ul>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <h6 className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-primary">Admin</span>
                  </h6>
                  <ul className="list-unstyled small text-muted mb-0">
                    <li><i className="bi bi-check text-success me-1"></i>Manage members</li>
                    <li><i className="bi bi-check text-success me-1"></i>Manage assets</li>
                    <li><i className="bi bi-check text-success me-1"></i>Run security scans</li>
                    <li><i className="bi bi-check text-success me-1"></i>View all data</li>
                  </ul>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <h6 className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-success">Editor</span>
                  </h6>
                  <ul className="list-unstyled small text-muted mb-0">
                    <li><i className="bi bi-check text-success me-1"></i>Edit assets</li>
                    <li><i className="bi bi-check text-success me-1"></i>Run scans</li>
                    <li><i className="bi bi-check text-success me-1"></i>View analytics</li>
                    <li><i className="bi bi-x text-danger me-1"></i>Manage members</li>
                  </ul>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <h6 className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-secondary">Viewer</span>
                  </h6>
                  <ul className="list-unstyled small text-muted mb-0">
                    <li><i className="bi bi-check text-success me-1"></i>View analytics</li>
                    <li><i className="bi bi-check text-success me-1"></i>View security logs</li>
                    <li><i className="bi bi-x text-danger me-1"></i>Edit assets</li>
                    <li><i className="bi bi-x text-danger me-1"></i>Run scans</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invite Team Member</h5>
                <button type="button" className="btn-close" onClick={() => setShowInviteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="colleague@example.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <div className="form-text">
                    The user will receive an email invitation to join your organization.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleInviteMember}>
                  <i className="bi bi-send me-2"></i>Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedMember && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Member Role</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Member</label>
                  <div className="bg-light p-3 rounded">
                    <div className="fw-semibold">{selectedMember.name}</div>
                    <div className="text-muted small">{selectedMember.email}</div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={selectedMember.role}
                    onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleUpdateRole}>
                  Update Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default UserManagementPage;
